import { useState, useMemo } from "react";
import { Table as TableIcon, ChartBar, SquaresFour, Info } from "@phosphor-icons/react";
import { NUTRIENT_LABELS, NUTRIENT_UNITS, NUTRIENT_CATEGORIES, STANDARD_CHART_KEYS } from "../lib/api";
import { NUTRIENT_INFO } from "../lib/nutrientInfo";
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from "./ui/tooltip";

const NutrientTip = ({ k, dri, testId }) => {
  const info = NUTRIENT_INFO[k];
  if (!info) return null;
  const target = Number(dri?.[k]) > 0 ? `${Number(dri[k]).toLocaleString()} ${NUTRIENT_UNITS[k] || ""}` : info.target;
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button type="button" aria-label={`About ${NUTRIENT_LABELS[k]}`} data-testid={testId}
          className="absolute top-2 right-2 text-neutral-400 hover:text-black transition-colors">
          <Info size={14} weight="fill" />
        </button>
      </TooltipTrigger>
      <TooltipContent className="max-w-[240px] bg-black text-white border-2 border-[#C8FF3F] rounded-xl p-3 space-y-1.5" data-testid={`${testId}-content`}>
        <div className="font-display font-black text-sm text-[#C8FF3F]">{NUTRIENT_LABELS[k]}</div>
        <p className="text-xs leading-snug">{info.role}</p>
        <p className="text-[11px] leading-snug text-white/80"><span className="font-mono uppercase text-[9px] tracking-wider text-[#C8FF3F]">Sources · </span>{info.sources}</p>
        <p className="text-[11px] text-white/80"><span className="font-mono uppercase text-[9px] tracking-wider text-[#C8FF3F]">Daily target · </span>{target}</p>
      </TooltipContent>
    </Tooltip>
  );
};

// Dashboard-tile emoji + accent per nutrient — makes the dashboard feel like a health tracker.
const NUTRIENT_ACCENT = {
  calories:   { emoji: "🔥", ring: "#C8FF3F" },
  protein:    { emoji: "💪", ring: "#C8FF3F" },
  carbs:      { emoji: "🌾", ring: "#FFD84D" },
  fat:        { emoji: "🥑", ring: "#FF7A45" },
  fiber:      { emoji: "🌱", ring: "#34D399" },
  sugar:      { emoji: "🍭", ring: "#F472B6" },
  saturated_fat: { emoji: "🧈", ring: "#F97316" },
  trans_fat:     { emoji: "⚠️", ring: "#DC2626" },
  mono_fat:      { emoji: "🫒", ring: "#84CC16" },
  poly_fat:      { emoji: "🐟", ring: "#38BDF8" },
  omega3:        { emoji: "🐟", ring: "#0EA5E9" },
  cholesterol:   { emoji: "🩸", ring: "#F87171" },
  vitamin_a:  { emoji: "🥕", ring: "#FB923C" },
  vitamin_b1: { emoji: "🌾", ring: "#FBBF24" },
  vitamin_b2: { emoji: "🥚", ring: "#F59E0B" },
  vitamin_b3: { emoji: "🍞", ring: "#EAB308" },
  vitamin_b5: { emoji: "🥑", ring: "#84CC16" },
  vitamin_b6: { emoji: "🍌", ring: "#FDE047" },
  vitamin_b7: { emoji: "🥚", ring: "#F59E0B" },
  folate:     { emoji: "🥬", ring: "#22C55E" },
  vitamin_b12:{ emoji: "🐟", ring: "#EF4444" },
  choline:    { emoji: "🥚", ring: "#F97316" },
  vitamin_c:  { emoji: "🍊", ring: "#FB923C" },
  vitamin_d:  { emoji: "☀️", ring: "#FBBF24" },
  vitamin_e:  { emoji: "🌰", ring: "#B45309" },
  vitamin_k:  { emoji: "🥬", ring: "#16A34A" },
  calcium:    { emoji: "🦴", ring: "#60A5FA" },
  phosphorus: { emoji: "🍖", ring: "#A855F7" },
  magnesium:  { emoji: "🥜", ring: "#8B5CF6" },
  sodium:     { emoji: "🧂", ring: "#F87171" },
  potassium:  { emoji: "🍌", ring: "#FDE047" },
  chloride:   { emoji: "🧂", ring: "#38BDF8" },
  sulfur:     { emoji: "🧄", ring: "#EAB308" },
  iron:       { emoji: "🩸", ring: "#DC2626" },
  zinc:       { emoji: "🛡️", ring: "#94A3B8" },
  copper:     { emoji: "🟠", ring: "#EA580C" },
  manganese:  { emoji: "🌰", ring: "#A16207" },
  iodine:     { emoji: "🌊", ring: "#0EA5E9" },
  selenium:   { emoji: "🌰", ring: "#EAB308" },
  molybdenum: { emoji: "🫘", ring: "#7C3AED" },
  chromium:   { emoji: "🥦", ring: "#059669" },
  boron:      { emoji: "🍎", ring: "#F43F5E" },
  cobalt:     { emoji: "💎", ring: "#3B82F6" },
  fluoride:   { emoji: "💧", ring: "#22D3EE" },
};

const DEFAULT_ACCENT = { emoji: "🧪", ring: "#94A3B8" };

/**
 * NutrientView — reusable table + infographic renderer.
 *
 * Props:
 *   nutrients   — object with { key: value } for every nutrient (per serving/total).
 *   dri         — optional object with target { key: value } for %-of-DRI computation.
 *   defaultTab  — 'standard' | 'energy' | 'macros' | 'vitamins' | 'macrominerals' | 'trace_minerals'
 *   defaultView — 'dashboard' | 'chart' | 'table'
 *   compact     — reduces vertical spacing.
 *   testIdPrefix— data-testid prefix.
 */
export default function NutrientView({
  nutrients = {},
  dri = null,
  defaultTab = "standard",
  defaultView = "dashboard",
  compact = false,
  testIdPrefix = "nutrient-view",
}) {
  const [tab, setTab] = useState(defaultTab);
  const [view, setView] = useState(defaultView);

  const tabs = useMemo(() => ([
    { key: "standard",       label: "Standard",       keys: STANDARD_CHART_KEYS },
    { key: "energy",         label: "Energy",         keys: NUTRIENT_CATEGORIES.energy.keys },
    { key: "macros",         label: "Macros",         keys: NUTRIENT_CATEGORIES.macros.keys },
    { key: "vitamins",       label: "Vitamins",       keys: NUTRIENT_CATEGORIES.vitamins.keys },
    { key: "macrominerals",  label: "Macrominerals",  keys: NUTRIENT_CATEGORIES.macrominerals.keys },
    { key: "trace_minerals", label: "Trace minerals", keys: NUTRIENT_CATEGORIES.trace_minerals.keys },
  ]), []);

  const activeKeys = tabs.find(t => t.key === tab)?.keys || STANDARD_CHART_KEYS;

  const pct = (k) => {
    if (!dri) return null;
    const target = Number(dri?.[k]) || 0;
    if (target <= 0) return null;
    return Math.round(((Number(nutrients?.[k]) || 0) / target) * 100);
  };

  const barTone = (p) => {
    if (p == null) return "bg-neutral-800";
    if (p < 40) return "bg-red-400";
    if (p < 70) return "bg-amber-400";
    if (p < 120) return "bg-emerald-400";
    if (p < 160) return "bg-sky-400";
    return "bg-fuchsia-400";
  };

  return (
    <div className="border-2 border-black rounded-2xl bg-white overflow-hidden" data-testid={testIdPrefix}>
      {/* Header controls */}
      <div className="flex flex-wrap items-center justify-between gap-2 border-b-2 border-black px-4 py-3 bg-black text-white">
        <div className="flex items-center gap-1 overflow-x-auto" data-testid={`${testIdPrefix}-tabs`}>
          {tabs.map(t => (
            <button key={t.key} onClick={() => setTab(t.key)}
              data-testid={`${testIdPrefix}-tab-${t.key}`}
              className={`px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.18em] rounded-full border transition-colors duration-150 whitespace-nowrap ${
                tab === t.key
                  ? "bg-[#C8FF3F] text-black border-[#C8FF3F]"
                  : "border-white/25 text-white/70 hover:text-white hover:border-white/60"
              }`}>
              {t.label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-1 shrink-0" role="tablist" aria-label="View format">
          <button onClick={() => setView("dashboard")} data-testid={`${testIdPrefix}-view-dashboard`}
            className={`h-8 px-3 flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.18em] rounded-full border transition-colors ${
              view === "dashboard" ? "bg-[#C8FF3F] text-black border-[#C8FF3F]" : "border-white/25 text-white/70 hover:text-white"
            }`}><SquaresFour size={12} weight="fill" /> Dashboard</button>
          <button onClick={() => setView("chart")} data-testid={`${testIdPrefix}-view-chart`}
            className={`h-8 px-3 flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.18em] rounded-full border transition-colors ${
              view === "chart" ? "bg-[#C8FF3F] text-black border-[#C8FF3F]" : "border-white/25 text-white/70 hover:text-white"
            }`}><ChartBar size={12} weight="fill" /> Chart</button>
          <button onClick={() => setView("table")} data-testid={`${testIdPrefix}-view-table`}
            className={`h-8 px-3 flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.18em] rounded-full border transition-colors ${
              view === "table" ? "bg-[#C8FF3F] text-black border-[#C8FF3F]" : "border-white/25 text-white/70 hover:text-white"
            }`}><TableIcon size={12} weight="fill" /> Table</button>
        </div>
      </div>

      {/* Body */}
      <div className={compact ? "p-3" : "p-4 md:p-5"} data-testid={`${testIdPrefix}-body`}>
        {view === "dashboard" ? (
          <TooltipProvider delayDuration={150}>
          <div className="grid gap-3 grid-cols-2 md:grid-cols-3 lg:grid-cols-4" data-testid={`${testIdPrefix}-dashboard`}>
            {activeKeys.map(k => {
              const val = Number(nutrients?.[k]) || 0;
              const p = pct(k);
              const accent = NUTRIENT_ACCENT[k] || DEFAULT_ACCENT;
              const clampedPct = p == null ? null : Math.min(100, Math.max(0, p / 2));
              // Build ring using CSS conic-gradient for a health-tracker look.
              const ringBg = p == null
                ? `conic-gradient(from 0deg, ${accent.ring} 0deg, ${accent.ring} 0deg, rgba(0,0,0,0.06) 0deg 360deg)`
                : `conic-gradient(from 0deg, ${accent.ring} 0deg ${(clampedPct / 100) * 360}deg, rgba(0,0,0,0.06) ${(clampedPct / 100) * 360}deg 360deg)`;
              const tone = p == null ? "neutral"
                : p < 40 ? "low" : p < 70 ? "med" : p < 120 ? "good" : p < 160 ? "high" : "over";
              const toneChip = {
                neutral: "bg-neutral-100 text-neutral-500",
                low:     "bg-red-100 text-red-700",
                med:     "bg-amber-100 text-amber-700",
                good:    "bg-emerald-100 text-emerald-700",
                high:    "bg-sky-100 text-sky-700",
                over:    "bg-fuchsia-100 text-fuchsia-700",
              }[tone];
              return (
                <div key={k}
                  className="relative border border-neutral-200 rounded-2xl p-3 pr-6 flex items-center gap-3 hover:border-black transition-colors"
                  data-testid={`${testIdPrefix}-tile-${k}`}
                >
                  <NutrientTip k={k} dri={dri} testId={`${testIdPrefix}-tip-${k}`} />
                  {/* Ring */}
                  <div className="relative shrink-0" style={{ width: 60, height: 60 }}>
                    <div
                      className="w-full h-full rounded-full"
                      style={{ background: ringBg }}
                    />
                    <div className="absolute inset-1.5 rounded-full bg-white flex items-center justify-center">
                      <span className="text-xl">{accent.emoji}</span>
                    </div>
                  </div>
                  {/* Text */}
                  <div className="min-w-0 flex-1">
                    <div className="font-mono text-[9px] uppercase tracking-[0.18em] text-neutral-500 truncate" title={NUTRIENT_LABELS[k]}>
                      {NUTRIENT_LABELS[k] || k}
                    </div>
                    <div className="mt-0.5 font-display font-black text-xl leading-none tabular-nums">
                      {val.toLocaleString(undefined, { maximumFractionDigits: val < 10 ? 2 : 1 })}
                      <span className="text-[10px] font-mono ml-1 opacity-60">{NUTRIENT_UNITS[k]}</span>
                    </div>
                    {p != null && (
                      <span className={`mt-1 inline-block px-1.5 py-0.5 rounded-full text-[9px] font-mono uppercase tracking-wide ${toneChip}`}>
                        {p}% DRI
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          </TooltipProvider>
        ) : view === "chart" ? (
          <div className="space-y-2" data-testid={`${testIdPrefix}-chart`}>
            {activeKeys.map(k => {
              const val = Number(nutrients?.[k]) || 0;
              const p = pct(k);
              const displayPct = p == null ? null : Math.min(200, p);
              return (
                <div key={k} className="grid grid-cols-[minmax(120px,180px)_1fr_60px] items-center gap-3">
                  <div className="text-[13px] font-medium truncate" title={NUTRIENT_LABELS[k]}>{NUTRIENT_LABELS[k] || k}</div>
                  <div className="relative h-5 bg-neutral-100 rounded-full overflow-hidden">
                    <div
                      className={`h-full ${barTone(p)} transition-all duration-500`}
                      style={{ width: `${displayPct == null ? Math.min(100, val > 0 ? 40 : 0) : Math.min(100, displayPct / 2)}%` }}
                    />
                    {dri && p != null && (
                      <div className="absolute inset-0 flex items-center justify-end pr-2 font-mono text-[10px] text-black/60">
                        {p}% DRI
                      </div>
                    )}
                  </div>
                  <div className="text-right font-mono text-[12px] whitespace-nowrap">
                    {val.toLocaleString(undefined, { maximumFractionDigits: 2 })}
                    <span className="text-[10px] text-neutral-500 ml-0.5">{NUTRIENT_UNITS[k] || ""}</span>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="overflow-x-auto -mx-4 md:mx-0" data-testid={`${testIdPrefix}-table`}>
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="border-b-2 border-black">
                  <th className="text-left font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500 px-3 py-2">Nutrient</th>
                  <th className="text-right font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500 px-3 py-2">Value</th>
                  <th className="text-right font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500 px-3 py-2">Unit</th>
                  {dri && <th className="text-right font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500 px-3 py-2">DRI</th>}
                  {dri && <th className="text-right font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500 px-3 py-2">% DRI</th>}
                </tr>
              </thead>
              <tbody>
                {activeKeys.map(k => {
                  const val = Number(nutrients?.[k]) || 0;
                  const target = Number(dri?.[k]) || 0;
                  const p = target > 0 ? Math.round((val / target) * 100) : null;
                  return (
                    <tr key={k} className="border-b border-neutral-200 hover:bg-neutral-50" data-testid={`${testIdPrefix}-row-${k}`}>
                      <td className="px-3 py-2 font-medium">{NUTRIENT_LABELS[k] || k}</td>
                      <td className="px-3 py-2 text-right font-mono tabular-nums">{val.toLocaleString(undefined, { maximumFractionDigits: 2 })}</td>
                      <td className="px-3 py-2 text-right font-mono text-neutral-500">{NUTRIENT_UNITS[k] || ""}</td>
                      {dri && <td className="px-3 py-2 text-right font-mono text-neutral-500">{target ? target.toLocaleString(undefined, { maximumFractionDigits: 2 }) : "—"}</td>}
                      {dri && <td className={`px-3 py-2 text-right font-mono ${p == null ? "text-neutral-400" : p < 50 ? "text-red-600" : p < 100 ? "text-amber-600" : "text-emerald-600"}`}>{p == null ? "—" : `${p}%`}</td>}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
